import { config } from "./config";
import { Feature, View, Map } from "ol";
import { Vector as VectorSource, Cluster as ClusterSource, TileWMS, XYZ } from "ol/source";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import TileGrid from "ol/tilegrid/TileGrid";
import { defaults as defaultControls } from "ol/control";
import { getPolygonSymbol, getSimpleSymbol } from "./symbols";
import { Basemaps } from "./enums";
import "./snapshots.scss";
import { ServerType } from "ol/source/wms";
import { MultiPolygon, Point, Polygon } from "ol/geom";

const shadowMapId = "shadow-map";

export const generateSnapshots = (
    feature: Feature,
    basemap: Basemaps,
    setSnapshotBig: CallableFunction,
    setSnapshotSmall: CallableFunction
): void => {
    const source = new VectorSource({ features: [feature] });
    const shadowMap = createShadowMap(basemap, source);

    shadowMap.once("rendercomplete", () => {
        setSnapshotBig(getSnapshot(shadowMap));
        shadowMap.getView().fit(source.getExtent(), {
            padding: [50, 50, 50, 50],
            callback: () =>
                shadowMap.once("rendercomplete", () => {
                    setSnapshotSmall(getSnapshot(shadowMap));
                    document.getElementById(shadowMapId).remove();
                })
        });
    });
};

const createShadowMap = (basemapId: Basemaps, source: VectorSource) => {
    const target = document.createElement("div");
    target.id = shadowMapId;
    document.body.append(target);

    return new Map({
        layers: [createBasemapLayer(basemapId), createPolygonLayer(source), createPointLayer(source)],
        target,
        view: new View({
            projection: config.default.projection,
            center: config.default.center,
            zoom: 3.55
        }),
        controls: defaultControls({ zoom: false, rotate: false })
    });
};

const createBasemapLayer = (basemapId: Basemaps) =>
    basemapId === Basemaps.Orto
        ? new TileLayer({
              source: new TileWMS({
                  url: config.basemapOrto.source.url,
                  params: config.basemapOrto.source.params,
                  projection: config.basemapOrto.source.projection,
                  serverType: config.basemapOrto.source.serverType as ServerType,
                  crossOrigin: config.basemapOrto.source.crossOrigin
              })
          })
        : new TileLayer({
              source: new XYZ({
                  url: config.basemapLVM.source.url,
                  maxZoom: config.basemapLVM.source.maxZoom,
                  projection: config.basemapLVM.source.projection,
                  tileGrid: new TileGrid(config.basemapLVM.tileGrid),
                  crossOrigin: config.basemapLVM.source.crossOrigin
              })
          });

const createPolygonLayer = (source: VectorSource) =>
    new VectorLayer({
        properties: { id: "shadow-polygon" },
        source,
        minZoom: 6,
        style: (f: Feature) => getPolygonSymbol(f, 6)
    });

const createPointLayer = (source: VectorSource) =>
    new VectorLayer({
        properties: { id: "shadow-point" },
        source: new ClusterSource({
            source,
            geometryFunction: (feature: Feature) => {
                const geometry = feature.getGeometry();
                const type = geometry.getType();
                return type === "Polygon"
                    ? (geometry as Polygon).getInteriorPoint()
                    : type === "MultiPolygon"
                    ? (geometry as MultiPolygon).getInteriorPoints().getPoint(1)
                    : (geometry as Point);
            }
        }),
        maxZoom: 6,
        style: getSimpleSymbol(true, 10)
    });

const getSnapshot = (shadowMap: Map) => {
    const mapCanvas = document.createElement("canvas");
    const size = shadowMap.getSize();
    mapCanvas.width = size[0];
    mapCanvas.height = size[1];
    const mapContext = mapCanvas.getContext("2d");
    Array.prototype.forEach.call(document.querySelectorAll(".ol-layer canvas"), (canvas) => {
        if (canvas.width > 0) {
            const opacity = canvas.parentNode.style.opacity;
            mapContext.globalAlpha = opacity === "" ? 1 : Number(opacity);
            const transform = canvas.style.transform;
            // Get the transform parameters from the style's transform matrix
            const matrix = transform
                .match(/^matrix\(([^\(]*)\)$/)[1]
                .split(",")
                .map(Number);
            // Apply the transform to the export map context
            CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
            mapContext.drawImage(canvas, 0, 0);
        }
    });

    return mapCanvas.toDataURL();
};
